import {Component, inject, Input} from '@angular/core';
import {ChangePartitionEvent} from "../../../events/ChangePartitionEvent";
import {PartitionSelectionService} from "../../../services/partition/partition-selection.service";
import {Paths} from 'src/app/utils/Paths';
import {BaseComponent} from "../../BaseComponent";
import {UserMetricGroup} from "../../../models/user/Metric/UserMetricGroup";
import {ClickEvent} from "devextreme/ui/button";
import {DxSelectBoxComponent} from "devextreme-angular";
import {DeleteChartEvent} from "../../../events/DeleteChartEvent";
import {DashboardService} from "../../../services/dashboard/dashboard.service";
import {Dashboard} from "../../../models/dashboard/Dashboard";
import {DeleteDashboardEvent} from "../../../events/DeleteDashboardEvent";
import {ReloadDataEvent} from "../../../events/ReloadDataEvent";
import {ShowSelectedDashboardEvent} from "../../../events/ShowSelectedDashboardEvent";
import {ChangeImpersonatedUserEvent} from "../../../events/ChangeImpersonatedUserEvent";
import {User} from "../../../models/user/User";
import {formatMessage} from "devextreme/localization";

@Component({
  selector: 'app-topbar-selectors',
  templateUrl: './topbar-selectors.component.html',
  styleUrls: ['./topbar-selectors.component.css']
})
export class TopbarSelectorsComponent extends BaseComponent {
  @Input('disabled') public disabled!: boolean;
  public partitionSelection: PartitionSelectionService = inject(PartitionSelectionService);
  public dashboardService: DashboardService = inject(DashboardService);
  /**
   * Flag used to block false positive reload requests
   * @protected
   */
  protected realReload = false;
  protected popupVisible = false;
  protected duplicatingDashboard?: Dashboard;
  protected chartPopupVisible = false;
  protected duplicatingChart?: UserMetricGroup;
  protected readonly Paths = Paths;

  protected get defaultPlaceholder(): string {
    return formatMessage('Select') ?? '';
  }

  /**
   * The selector also emits its current value on init, so unchanged values are skipped to avoid
   * reloading the page that just loaded it.
   */
  changePartition(partitionId: number | null | undefined) {
    if (partitionId === null || partitionId === undefined || partitionId === this.partitionSelection.selectedPartitionId)
      return;
    this.partitionSelection.selectedPartitionId = partitionId;
    this.eventBus.emitEvent(new ChangePartitionEvent());
  }

  isPartitionSelectorShown(): boolean {
    return this.appService.menuVoice?.name === Paths.DIAGNOSTICS
      || this.appService.menuVoice?.name === Paths.ALERTS
      || this.appService.menuVoice?.name === Paths.GATEWAYS
      || this.appService.menuVoice?.name === Paths.VIRTUAL_METRICS;
  }

  /**
   * Checks if the reload requests is real or a false positive
   * @param item
   */
  checkForRealReload(item: UserMetricGroup) {
    this.realReload = !this.dashboardService.selUserMetricGroup || item.id !== this.dashboardService.selUserMetricGroup.id;
    this.dashboardService.selUserMetricGroup = item;
    this.reloadChart()
  }


  /**
   * Permanently deletes the metric group by calling the delUserMetricGroup API and then reloads.
   * @param $event
   * @param item
   * @param selectBox
   */
  async deleteMetricGroup($event: ClickEvent, item: UserMetricGroup, selectBox: DxSelectBoxComponent) {
    if (!!$event.event) {
      $event.event.stopPropagation();
      this.showDeleteDialog(item.name).show().done(
        async (result: boolean) => {
          if (result) {
            this.eventBus.emitEvent(new DeleteChartEvent(item.id));
            selectBox.instance.close();
          }
        });
    }
  }

  /**
   * Only fired when a copy landed on the user whose dashboards are on screen.
   */
  reloadDashboardsAfterDuplicate() {
    this.dashboardService.getUserDashboards();
  }

  /**
   * Only fired when a copy landed on the user whose charts are on screen. The chart page owns the
   * "select one, then load its data" flow, so it reloads on the event rather than here.
   */
  reloadChartsAfterDuplicate() {
    this.dashboardService.getUserMetricGroups().then(() => this.eventBus.emitEvent(new ReloadDataEvent()));
  }

  openDuplicateDashboard($event: any, dashboard: Dashboard) {
    $event.event?.stopPropagation();
    this.duplicatingDashboard = dashboard;
    this.popupVisible = true;
  }

  openDuplicateChart($event: any, chart: UserMetricGroup) {
    $event.event?.stopPropagation();
    this.duplicatingChart = chart;
    this.chartPopupVisible = true;
  }

  deleteDashboard($event: any, dashboard: Dashboard) {
    if (!!$event.event) {
      $event.event.stopPropagation();
      this.eventBus.emitEvent(new DeleteDashboardEvent(dashboard));
    }
  }

  reloadChart() {
    if (this.realReload)
      this.eventBus.emitEvent(new ReloadDataEvent());
    this.realReload = false;
  }

  /**
   * Switches the user whose dashboards or charts are shown — the selection is shared, and both
   * pickers go through here. The reload is left to whichever page is open, which owns its own
   * default-selection logic.
   */
  changeImpersonatedUser(user: User) {
    if (!!user)
      this.impersonateUser(user.id);
  }

  returnToOwnUser() {
    this.impersonateUser(this.gmService.user.id);
  }

  private impersonateUser(userId: number) {
    if (userId === this.dashboardService.impersonatedUserId)
      return;

    this.dashboardService.setImpersonatedUser(userId);
    this.eventBus.emitEvent(new ChangeImpersonatedUserEvent());
  }

  loadSelectedDashboard(dashboard: Dashboard) {
    this.dashboardService.selectedDashboard = dashboard;
    this.eventBus.emitEvent(new ShowSelectedDashboardEvent());
  }

  isShown(): boolean {
    return this.appService.menuVoice?.name === Paths.DASHBOARD
    || this.appService.menuVoice?.name === Paths.CHARTS
    || this.appService.menuVoice?.name === Paths.DIAGNOSTICS
    || this.appService.menuVoice?.name === Paths.ALERTS
    || this.appService.menuVoice?.name === Paths.GATEWAYS
    || this.appService.menuVoice?.name === Paths.VIRTUAL_METRICS
  }
}
